import React from "react";
import { View, Text, StyleSheet, SafeAreaView, StatusBar } from "react-native";
import { FontFamilies } from "@/constants/Typography";
import { ActionButton } from "@/components/ActionButton";
import { router, Stack } from "expo-router";
import { Image } from "expo-image";

const doordashLogo = require("@/assets/images/logo-doordash.png");
const amazonLogo = require("@/assets/images/logo-amazon.png");
const unitedLogo = require("@/assets/images/logo-united.png");
const spotifyLogo = require("@/assets/images/logo-spotify.png");

interface Transaction {
  id: string;
  name: string;
  category: string;
  categoryEmoji: string;
  categoryColor: string;
  amount: string;
  logo: any;
}

const TRANSACTIONS: Transaction[] = [
  {
    id: "doordash",
    name: "DoorDash",
    category: "Food & Drink",
    categoryEmoji: "🍽️",
    categoryColor: "rgba(252, 202, 166, 0.3)",
    amount: "$15.91",
    logo: doordashLogo,
  },
  {
    id: "amazon",
    name: "Amazon",
    category: "Merchandise",
    categoryEmoji: "🛍️",
    categoryColor: "rgba(67, 191, 244, 0.3)",
    amount: "$19.95",
    logo: amazonLogo,
  },
  {
    id: "united",
    name: "United Airlines",
    category: "Travel",
    categoryEmoji: "✈️",
    categoryColor: "#FFFACC",
    amount: "$209.90",
    logo: unitedLogo,
  },
  {
    id: "spotify",
    name: "Spotify",
    category: "Entertainment",
    categoryEmoji: "🎬",
    categoryColor: "rgba(255, 112, 198, 0.2)",
    amount: "$5.90",
    logo: spotifyLogo,
  },
];

const MoneyTrackingIntroScreen = () => {
  const handleContinue = () => {
    router.push("/signup/set-monthly-budget");
  };

  return (
    <View style={styles.container}>
      <Stack.Screen options={{ headerShown: false, gestureEnabled: false }} />
      <StatusBar barStyle="dark-content" />
      <SafeAreaView style={styles.safeArea}>
        {/* Progress Bar */}
        <View style={styles.progressContainer}>
          <View style={styles.progressBar}>
            <View style={styles.progressFill} />
          </View>
        </View>

        {/* Content */}
        <View style={styles.content}>
          {/* Header */}
          <View style={styles.headerSection}>
            <Text style={styles.sectionLabel}>Money tracking</Text>
            <Text style={styles.title}>See where your money goes</Text>
            <Text style={styles.subtitle}>
              Money tracking helps you understand your full financial picture in
              one place.
            </Text>
          </View>

          {/* Transaction Cards */}
          <View style={styles.cardsContainer}>
            {/* Background card (offset) */}
            <View style={styles.backgroundCard}>
              <View style={styles.transactionRow}>
                <Image source={amazonLogo} style={styles.transactionLogo} />
                <View style={styles.transactionInfo}>
                  <View style={styles.transactionNameRow}>
                    <Text style={styles.transactionName}>Amazon</Text>
                    <View
                      style={[
                        styles.categoryBadge,
                        { backgroundColor: "rgba(67, 191, 244, 0.3)" },
                      ]}
                    >
                      <Text style={styles.categoryText}>🛍️ Merchandise</Text>
                    </View>
                  </View>
                </View>
                <Text style={styles.transactionAmount}>$19.95</Text>
              </View>
            </View>

            {/* Main card */}
            <View style={styles.mainCard}>
              {TRANSACTIONS.map((transaction, index) => (
                <View
                  key={transaction.id}
                  style={
                    transaction.id === "united" ? { marginTop: 20 } : undefined
                  }
                >
                  <View style={styles.transactionRow}>
                    {transaction.id === "spotify" ? (
                      <View style={styles.spotifyLogoContainer}>
                        <Image
                          source={transaction.logo}
                          style={styles.spotifyLogo}
                          contentFit="contain"
                        />
                      </View>
                    ) : (
                      <Image
                        source={transaction.logo}
                        style={styles.transactionLogo}
                      />
                    )}
                    <View style={styles.transactionInfo}>
                      <View style={styles.transactionNameRow}>
                        <Text style={styles.transactionName}>
                          {transaction.name}
                        </Text>
                        <View
                          style={[
                            styles.categoryBadge,
                            { backgroundColor: transaction.categoryColor },
                          ]}
                        >
                          <Text style={styles.categoryText}>
                            {transaction.categoryEmoji} {transaction.category}
                          </Text>
                        </View>
                      </View>
                    </View>
                    <Text style={styles.transactionAmount}>
                      {transaction.amount}
                    </Text>
                  </View>
                  {index < TRANSACTIONS.length - 1 && (
                    <View style={styles.separator} />
                  )}
                </View>
              ))}
            </View>
          </View>
        </View>

        {/* Spacer */}
        <View style={styles.spacer} />

        {/* Bottom Section */}
        <View style={styles.bottomSection}>
          <View style={styles.buttonContainer}>
            <ActionButton title="Continue" onPress={handleContinue} />
          </View>
        </View>
      </SafeAreaView>
    </View>
  );
};

export default MoneyTrackingIntroScreen;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#FFFFFF",
  },
  safeArea: {
    flex: 1,
  },
  progressContainer: {
    paddingHorizontal: 24,
    paddingTop: 10,
    paddingBottom: 16,
  },
  progressBar: {
    height: 6,
    backgroundColor: "#E1EFE4",
    borderRadius: 100,
  },
  progressFill: {
    width: 16,
    height: 6,
    backgroundColor: "rgba(22, 29, 20, 0.5)",
    borderRadius: 100,
  },
  content: {
    flex: 1,
    paddingHorizontal: 16,
  },
  headerSection: {
    gap: 12,
    marginBottom: 24,
  },
  sectionLabel: {
    fontFamily: FontFamilies.Inter.Medium,
    fontSize: 16,
    lineHeight: 22,
    color: "#0F9129",
    letterSpacing: -0.32,
  },
  title: {
    fontFamily: FontFamilies.Inter.SemiBold,
    fontSize: 32,
    lineHeight: 38,
    color: "#161D14",
    letterSpacing: -0.64,
  },
  subtitle: {
    fontFamily: FontFamilies.Inter.Regular,
    fontSize: 16,
    lineHeight: 24,
    color: "rgba(22, 29, 20, 0.7)",
    letterSpacing: 0.16,
  },
  cardsContainer: {
    marginTop: 100,
    position: "relative",
    alignItems: "center",
  },
  backgroundCard: {
    position: "absolute",
    top: 67,
    left: 8,
    right: 8,
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 11,
    shadowColor: "#181818",
    shadowOffset: { width: -3, height: 4 },
    shadowOpacity: 0.2,
    shadowRadius: 17,
    elevation: 8,
    zIndex: 0,
  },
  mainCard: {
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    paddingVertical: 12,
    paddingHorizontal: 12,
    width: "90%",
    opacity: 0.9,
    shadowColor: "#6F6F6F",
    shadowOffset: { width: -3, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 17,
    elevation: 5,
    zIndex: 1,
  },
  transactionRow: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 8,
    gap: 8,
  },
  transactionLogo: {
    width: 40,
    height: 40,
    borderRadius: 20,
  },
  spotifyLogoContainer: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: "#161D14",
    justifyContent: "center",
    alignItems: "center",
    overflow: "hidden",
  },
  spotifyLogo: {
    width: 40,
    height: 40,
  },
  transactionInfo: {
    flex: 1,
  },
  transactionNameRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  transactionName: {
    fontFamily: FontFamilies.Inter.SemiBold,
    fontSize: 15,
    lineHeight: 27,
    color: "#161D14",
  },
  categoryBadge: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 20,
  },
  categoryText: {
    fontFamily: FontFamilies.Inter.Medium,
    fontSize: 12,
    lineHeight: 23,
    color: "#000000",
  },
  transactionAmount: {
    fontFamily: FontFamilies.Inter.SemiBold,
    fontSize: 15,
    lineHeight: 27,
    color: "#161D14",
  },
  separator: {
    height: 1,
    backgroundColor: "#F3F3F3",
  },
  spacer: {
    flex: 1,
  },
  bottomSection: {
    paddingHorizontal: 16,
    borderTopWidth: 1,
    borderTopColor: "#F3F3F3",
    paddingTop: 12,
    backgroundColor: "#FFFFFF",
    zIndex: 10,
  },
  buttonContainer: {
    marginBottom: 12,
  },
});
